import React, { useState, useCallback } from "react";
import styled from '@emotion/styled';
import { isMobile } from 'react-device-detect';
import Card from '../components/Ui/Card.jsx';
import { ServerSidePagingTable } from "../components/Ui/Table.jsx";
import { Alert } from "../components/Ui/Alert.jsx";
import { fetchOpticiansEyeTicketsAwaitingProcessing, fetchOpticiansEyeTicketsAwaitingProcessingAdvancedSearch } from "../http/eyeticket.js";
import { processNonEyeTicketRequest } from "../http/optician.js";
import ProcessEyeTicket from "../components/Ui/EyeTicket/Opticians/ProcessEyeTicket.jsx";
import Modal from "../components/Ui/Modal.jsx";
import { InputContainer, Label } from "../components/StyledComponents/Table/TableComponents.jsx";

const StyledModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  padding: 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 80%;
  max-width: 500px;
`;

const ModalHeader = styled.h3`
  margin-top: 0;
  color: #333;
  text-align: center;
`;

const ModalBody = styled.div`
  margin: 20px 0;
`;

const WarningBox = styled.div`
  background-color: #fff3cd;
  border-left: 8px solid #ffeeba;
  padding: 15px;
  margin-bottom: 20px;
`;

const ModalFooter = styled.div`
  text-align: right;
`;

const CancelButton = styled.button`
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  margin-right: 10px;
  border-radius: 5px;
  cursor: pointer;
  &:hover {
    background-color: #5a6268;
  }
`;

const DeleteButton = styled(CancelButton)`
  background-color: #dc3545;
  &:hover {
    background-color: #c82333;
  }
`;

const ExpandedRowContainer = styled.div`
  background-color: #f9f9f9;
  border: 1px solid #ddd;
  padding:10px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.3);
`;

const SectionTitle = styled.h2`
  font-size: 1rem;
  margin-bottom: 8px;
  color: #333;
`;

const List = styled.ul`
  margin: 0;
  padding-left: 20px;
`;

const ListItem = styled.li`
  margin-bottom: 4px;
  font-size: 14px;
  color: #555;
`;

const ButtonGroup = styled.div`
  display: flex;
  width: 100%;
`;

const SelectInputStyled = styled.select`
  padding: 5px;
  border: 1px solid #E2E8F0;
  height: 33px;

  border-radius: 0.375rem;
  background-color: ${props => props.missing ? '#ffdddd' : '#fff'};
  width: 100%;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:focus {
    outline: none;
    border-color: #93C5FD;
    box-shadow: 0 0 0 3px rgba(147, 197, 253, 0.5);
  }
`;

const Button = styled.button`
  padding: 5px 10px;
  background-color: ${({ colours }) => colours[0]};
  color: white;
  font-size: 11px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  width:100%;
  transition: background-color 0.3s ease;


  &:hover {
    background-color: ${({ colours }) => colours[1]};
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const CenterContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100vh;
  width: 100%;
`;

const FormContainer = styled.div`
  border-radius: 5px;
  background-color: #fff;
  width: ${isMobile ? '100%' : '50%'};
`;

const Heading = styled.h3`
  margin: 0;
  margin-top: 10px;
  padding-bottom: 5px;
`;

const Paragraph = styled.p`
  margin: 0 0 20px 0;
  font-size:15px;
`;

const StyledLabel = styled.label`
  font-size: 12px;
  color: ${({ fontColour }) => fontColour};
`;

export default function EyeTicketsAwaitingProcessing({ colour }) {
  const [isNoGlassesModalOpen, setIsNoGlassesModalOpen] = useState(false);
  const [showEyeTicket, setShowEyeTicket] = useState(false);
  const [eyeTickets, setEyeTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [eyeticketCount, setEyeticketCount] = useState(0);
  const [currentEyeTicket, setCurrentEyeTicket] = useState(null);
  const [isTicketProcessed, setIsTicketProcessed] = useState(false);
  const [includeRecall, setIncludeRecall] = useState(false);

  const [pagingTracker, setPagingTracker] = useState(
    {
      currentPage: 1,
      pageSize: 10,
      filterStatus: null,
      searchValue: null,
      account: null,
      coWorker: null
    });

  function ShowHideProcessEyeTicket(eyeTicketId = null) {
    setShowEyeTicket(!showEyeTicket);

    if (eyeTicketId) {
      setEyeTickets(prevEyeTickets =>
        prevEyeTickets.filter(ticket => ticket.EyeTicketID !== eyeTicketId)
      );

      setIsTicketProcessed(true);
    } else {
      setIsTicketProcessed(false);
    }
  }

  const loadEyetickets = useCallback(async (account = null, page, pageSize, filterStatus = null, searchValue = "", coWorker = null) => {
    setIsLoading(true);

    if (page && pageSize) {
      try {
        setEyeTickets([]);
        setEyeticketCount(0);

        let fetchedEyetickets = [];

        if (includeRecall)
        {
          fetchedEyetickets = await fetchOpticiansEyeTicketsAwaitingProcessingAdvancedSearch(searchValue, coWorker);
        } else {
          fetchedEyetickets = await fetchOpticiansEyeTicketsAwaitingProcessing(page, pageSize, searchValue, coWorker);
        }

        if (!fetchedEyetickets.AwaitingProcessing) {
          setEyeTickets([]);
          setEyeticketCount(0);
        }

        if (fetchedEyetickets && fetchedEyetickets.AwaitingProcessing) {
          const restructuredEyeTickets = fetchedEyetickets.AwaitingProcessing.map(order => ({
            ...order,
          }));

          setEyeTickets(restructuredEyeTickets);
          setEyeticketCount(fetchedEyetickets.Count);
        }

        setError(null);
      } catch (e) {
        setError(e);
        setEyeTickets([]);
        setEyeticketCount(0);
      } finally {
        setIsLoading(false);
      }
    }
  }, [includeRecall]);

  const mobileColumns = [
    {
      Header: "Name",
      accessor: "PatientName",
      Cell: ({ row }) => {
        const fontColour = row.original.SightTestDetails.includes('Employee') ? 'Green' : 'Blue';
        return (
          <>
            <StyledLabel fontColour={fontColour}>{row.original.SightTestDetails}</StyledLabel>
            <p>{row.original.PatientName}</p>
          </>
        );
      }
    },
    {
      Header: "Eyewear",
      accessor: row => row.Eyewear,
    },
    {
      width: 300,
      Header: "Actions",
      accessor: "Action",
      Cell: ({ row }) => {
        return (
          <>
            <SelectInputStyled
              onChange={(event) => {
                const selectedValue = event.target.value;

                if (selectedValue !== "") {
                  setCurrentEyeTicket({
                    selectedEyeTicket: { ...row.original },
                    selectedAction: selectedValue,
                  });
                }
              }
              }
              value={currentEyeTicket?.selectedEyeTicket?.EyeTicketID === row.original.EyeTicketID && currentEyeTicket.selectedAction}
            >
              <option value="">Select</option>
              {row.original.Action?.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </SelectInputStyled>
            <Button
              disabled={!(currentEyeTicket?.selectedEyeTicket?.EyeTicketID === row.original.EyeTicketID)}
              colours={currentEyeTicket?.selectedAction !== 'No Glasses' ? ['#59bd35', '#428c27'] : ['#b03737', '#6e2121']}
              onClick={ProcessEyeTicket}
            >
              Process EyeTicket
            </Button>
          </>
        );
      },
    },
  ];

  const desktopColumns = [
    {
      Header: "Name",
      accessor: "PatientName",
      Cell: ({ row }) => {
        const fontColour =
          row.original.SightTestDetails.includes('Employee') ? 'Green' :
            row.original.SightTestDetails.includes('Sight Test Only') ? 'Purple' :
              'Blue';
        return (
          <>
            <StyledLabel fontColour={fontColour}>{row.original.SightTestDetails}</StyledLabel>
            <p>{row.original.PatientName}</p>
          </>
        );
      }
    },
    {
      Header: "Prescription No.",
      accessor: row => row.ID,
    },
    {
      Header: "Eyewear",
      accessor: row => row.Eyewear,
    },
    {
      width: 300,
      Header: "Actions",
      accessor: "Action",
      Cell: ({ row }) => {
        return (
          <>
            <SelectInputStyled
              onChange={(event) => {
                const selectedValue = event.target.value;

                if (selectedValue !== "") {
                  setCurrentEyeTicket({
                    selectedEyeTicket: { ...row.original },
                    selectedAction: selectedValue,
                  });
                }
              }
              }
              value={currentEyeTicket?.selectedEyeTicket?.EyeTicketID === row.original.EyeTicketID && currentEyeTicket.selectedAction}
            >
              <option value="">Select</option>
              {row.original.Action?.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </SelectInputStyled>
            <Button
              disabled={!(currentEyeTicket?.selectedEyeTicket?.EyeTicketID === row.original.EyeTicketID) || isLoading}
              colours={['#59bd35', '#428c27']}
              onClick={() => {
                if (currentEyeTicket?.selectedAction === 'Not Required') {
                  setIsNoGlassesModalOpen(true);
                } else if (currentEyeTicket?.selectedAction === 'Sight Test Only') {
                  handleSightTestOnly();
                } else {
                  setShowEyeTicket(true);
                }
              }}
            >
              Process EyeTicket
            </Button>
          </>
        );
      },
    },
  ];

  const columns = (isMobile ? desktopColumns : desktopColumns);

  async function handleSightTestOnly() {
    if (!currentEyeTicket) return;
    setIsLoading(true);
    try {
      var response = await processNonEyeTicketRequest(currentEyeTicket.selectedEyeTicket.EyeTicketID, currentEyeTicket.selectedEyeTicket.ID, currentEyeTicket.selectedEyeTicket.Eyewear, 'Sight Test Only');
      setCurrentEyeTicket(null);

      if (response.Message && response?.Message === "Your request for Sight Test Only has not been permitted / processed") {
        setWarning(response.Message);
        return;
      }
      loadEyetickets();
      setError(null);
      setWarning(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCancelEyeTicket() {
    if (!currentEyeTicket) return;
    setIsLoading(true);
    try {
      var response = await processNonEyeTicketRequest(currentEyeTicket.selectedEyeTicket.EyeTicketID, currentEyeTicket.selectedEyeTicket.ID, currentEyeTicket.selectedEyeTicket.Eyewear, 'Not Required');
      setCurrentEyeTicket(null);

      if (response.Message && response?.Message === "Your request for Not Required has not been permitted / processed") {
        setWarning(response.Message);
        return;
      }
      loadEyetickets();
      setError(null);
      setWarning(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }

  const deleteModal = currentEyeTicket && (
    <StyledModal>
      <ModalHeader>Not Required?</ModalHeader>
      <ModalBody>
        <WarningBox>
          <strong>Warning</strong>
          <p>By submitting this EyeTicket as Not Required, you won't be able to recover it.</p>
        </WarningBox>
        {currentEyeTicket && (
          <p>Are you sure you want to submit this ticket for <b>{currentEyeTicket.selectedEyeTicket.PatientName}?</b></p>
        )}
      </ModalBody>
      <ModalFooter>
        <CancelButton onClick={() => setIsNoGlassesModalOpen(false)}>No</CancelButton>
        <DeleteButton onClick={() => {
          handleCancelEyeTicket();
          setIsNoGlassesModalOpen(false);
        }}>Yes, Not Required</DeleteButton>
      </ModalFooter>
    </StyledModal>
  );

  const message = isTicketProcessed && currentEyeTicket
    ? (
      <>
        Successfully Processed EyeTicket with RX Number: <strong>{currentEyeTicket.selectedEyeTicket.EyeTicketID}</strong> for <strong>{currentEyeTicket.selectedEyeTicket.PatientName}</strong>.
        <br /><br />
        <strong>Please keep a copy of this prescription number.</strong> You can even pass it onto our patient so they can track the order.
        The eyewear order will now be manufactured to your specific requirements. <strong>Please allow 2-5 working days for delivery.</strong> If there are any problems with your safety eyewear order, we will contact you.
        <br /><br />
        If you select <strong>EyeTicket™ History & Tracking</strong> from the left menu, you can:
        <ul>
          <li>View the order history of EyeTickets processed.</li>
          <li>Track the status of the order.</li>
          <li>See exactly where your order is in our Optical Lab.</li>
        </ul>
        <br />
        <strong>Thank you once again for placing your order with MCR Safety Europe.</strong>
      </>
    )
    : 'Click on an order row to preview the Eyewear Profile.';

  return (
    <>
      {showEyeTicket &&
        <ProcessEyeTicket
          eyeTicketId={currentEyeTicket.selectedEyeTicket.EyeTicketID}
          barcode={currentEyeTicket.selectedEyeTicket.ID}
          eyewear={currentEyeTicket.selectedEyeTicket.Eyewear}
          showHideModal={ShowHideProcessEyeTicket}
          isModalOpen={showEyeTicket}
          colours={colour}
          sightTestInfo={currentEyeTicket.selectedEyeTicket.SightTestDetails} />}
      <Modal open={isNoGlassesModalOpen}>
        {currentEyeTicket && deleteModal}
      </Modal>
      <CenterContainer>
        <FormContainer>
          <Card
            label={'EyeTickets Awaiting Processing'}
            headercolour={colour.sidebar}
            headerTextColour={colour.font}>
            <Heading>EyeTicket's Awaiting Processing</Heading>
            <Paragraph>The EyeTickets below have been assigned to your optical practice for processing. If a patient has attended your practice in error, you can still recall their EyeTicket by setting “Advanced EyeTicket Recall” to “Yes” and entering their EyeTicket ID and surname in the search box.</Paragraph>
            <ServerSidePagingTable
              columns={columns}
              pagingFn={loadEyetickets}
              childrenContainerHeight={"70px"}
              children={
                <InputContainer>
                  <Label>Advanced EyeTicket Recall :{' '}</Label>
                  <SelectInputStyled
                    onChange={(e) => setIncludeRecall(e.target.value === 'true')}
                    value={includeRecall.toString()}
                  >
                    <option key={1} value="true">Yes</option>
                    <option key={0} value="false">No</option>
                  </SelectInputStyled>
                </InputContainer>}
              data={eyeTickets}
              recordCount={eyeticketCount}
              setPagingTracker={setPagingTracker}
              barcode={true}
              info={message}
              type={isTicketProcessed ? 'success' : 'info'}
              expand={(rowData) => (
                <ExpandedRowContainer>
                  <h1 style={{ fontSize: "1.1rem", marginBottom: "12px" }}>
                    Eyewear Profile for {rowData.PatientName}
                  </h1>
                  {rowData.EyewearProfile?.LensTypesAllowed.filter(x => x !== "").length > 0 &&
                    <>
                      <SectionTitle>Lens Types Allowed:</SectionTitle>
                      <List>
                        {rowData.EyewearProfile?.LensTypesAllowed.map((lens, index) => (
                          <ListItem key={index}>{lens}</ListItem>
                        ))}
                      </List>
                    </>
                  }
                  {rowData.EyewearProfile?.FramesAllowed.filter(x => x !== "").length > 0 &&
                    <>
                      <SectionTitle>Frames Allowed:</SectionTitle>
                      <List>
                        {rowData.EyewearProfile?.FramesAllowed.map((frame, index) => (
                          <ListItem key={index}>{frame}</ListItem>
                        ))}
                      </List>
                    </>
                  }
                  {rowData.EyewearProfile?.LensExtrasAllowed.filter(x => x !== "").length > 0 &&
                    <>
                      <SectionTitle>Lens Extras Allowed:</SectionTitle>
                      <List>
                        {rowData.EyewearProfile?.LensExtrasAllowed.map((extra, index) => (
                          <ListItem key={index}>{extra}</ListItem>
                        ))}
                      </List>
                    </>
                  }
                  {rowData.EyewearProfile?.SpecialInstructions.filter(x => x !== "").length > 0 &&
                    <>
                      <SectionTitle>Special Instructions:</SectionTitle>
                      <List>
                        {rowData.EyewearProfile?.SpecialInstructions.map((instruction, index) => (
                          <ListItem key={index}>{instruction}</ListItem>
                        ))}
                      </List>
                    </>
                  }
                </ExpandedRowContainer>
              )}
            >
            </ServerSidePagingTable>
            {error && !isLoading && <Alert type="error">Unable to render page due to <b>{error.message}</b></Alert>}
            {warning && <Alert type="warning"><b>{warning}</b></Alert>}
            {!eyeticketCount && <Alert type="information">No Eyetickets Found.</Alert>}
          </Card>
        </FormContainer>
      </CenterContainer>
    </>
  );
}